import React from "react";
import { Box, Button, Link, Typography } from "@mui/material";
import { useAuth } from "../../common/auth";
import { C } from "../../common/theme";
import { EmailContent, SentEmail, getEmailContent } from "../../common/requests/emails/endpoints";
import { Banner, CopyValue, DetailDialog, DetailList, DetailSection, LoadingRows, StatusPill } from "../../ui";
import EmailPreview from "./EmailPreview";
import { EVENT_LABEL, errorMessage, formatWhen, statusMeta } from "./emailData";

/** One email: who it went to, what happened to it, and what it looked like. */
export default function SentEmailDialog({ email, onClose }: { email: SentEmail | null; onClose: () => void }) {
  const { idToken } = useAuth();
  const [content, setContent] = React.useState<EmailContent | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!email || !idToken) return;
    let cancelled = false;
    setContent(null);
    setError(null);
    setLoading(true);
    getEmailContent(idToken, email.message_id)
      .then(({ data }) => !cancelled && setContent(data))
      .catch((err) => !cancelled && setError(errorMessage(err, "Couldn't load the email from Brevo.")))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [email, idToken]);

  if (!email) return null;
  const meta = statusMeta(email.status);

  return (
    <DetailDialog
      open
      onClose={onClose}
      size="lg"
      title={email.subject || "(no subject)"}
      subtitle={`To ${email.email}`}
      status={
        <StatusPill tone={meta.tone} title={meta.help}>
          {meta.label}
        </StatusPill>
      }
      actions={
        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      }
    >
      <DetailSection title="Details">
        <DetailList
          items={[
            { label: "Sent", value: formatWhen(email.sent_at) },
            { label: "Template", value: email.template_name ? `${email.template_name} (#${email.template_id})` : "None (sent as raw HTML)" },
            { label: "From", value: email.from || "–" },
            ...(email.tags.length > 0 ? [{ label: "Tags", value: email.tags.join(", ") }] : []),
            { label: "Message ID", value: <CopyValue value={email.message_id} mono />, full: true },
          ]}
        />
      </DetailSection>

      <DetailSection title="What happened">
        <Box component="ol" sx={{ m: 0, p: 0, listStyle: "none", display: "grid", gap: 0.75 }}>
          {email.events.map((e, i) => {
            const problem = statusMeta(e.status).tone === "danger";
            return (
              <Box component="li" key={i} sx={{ display: "flex", gap: 1.5, alignItems: "baseline", flexWrap: { xs: "wrap", sm: "nowrap" } }}>
                <Typography variant="caption" className="num" sx={{ color: C.muted, minWidth: 118, flexShrink: 0 }}>
                  {formatWhen(e.at)}
                </Typography>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" sx={{ color: problem ? C.danger : C.ink, fontWeight: problem ? 700 : 500 }}>
                    {EVENT_LABEL[e.event] ?? e.event}
                  </Typography>
                  {e.reason && (
                    <Typography variant="caption" component="div" sx={{ color: C.muted, overflowWrap: "anywhere" }}>
                      {e.reason}
                    </Typography>
                  )}
                  {e.link && (
                    <Link href={e.link} target="_blank" rel="noreferrer" variant="caption" sx={{ overflowWrap: "anywhere", color: C.ink }}>
                      {e.link}
                    </Link>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </DetailSection>

      <DetailSection title="The email">
        {loading && <LoadingRows rows={3} height={60} />}
        {error && <Banner tone="danger">{error}</Banner>}
        {content && !content.available && (
          <Banner>
            Brevo no longer keeps the content of this email. It only stores what was sent for a limited time; the delivery history above is kept longer.
          </Banner>
        )}
        {content && content.available && <EmailPreview html={content.html} title={email.subject || "Email"} />}
      </DetailSection>
    </DetailDialog>
  );
}
